import { Component, prop } from "@kluntje/core";
import { HIDDEN } from "Helper/cssClasses";
import { hasClass, toggleClass } from "@kluntje/js-utils/lib/dom-helpers";
import SSOService, { UserObject } from "Services/SSOService";
import { I18nService } from "Services/I18nService";
import { replacePlaceHolder } from "Helper/stringHelper";

/**
 * Custom Element to toggle options depending on the auth state
 */
class FRAUserButton extends Component {
  SSOService = SSOService.getInstance();
  I18nService = I18nService.getInstance();
  loggedIn?: boolean;

  /**
   * The configured URLs which are called by click logout-button
   * @see FraAccountConfig.logoutUrls
   */
  @prop({ type: "object", defaultValue: [] })
  urls: Array<{ url: string }> = [];

  /**
   * href to the account page with the login form
   */
  @prop({ type: "string", defaultValue: "" })
  loginPageUrl = "";

  constructor() {
    super({
      ui: {
        greeting: ".fra-m-user-button__greeting :-one",
        logoutBtn: ".fra-m-user-button__logout-btn :-one",
        opener: ".fra-m-user-button__opener :-one",
        tooltip: ".fra-m-user-button__tooltip :-one",
      },

      events: [
        {
          event: "click",
          target: "logoutBtn",
          handler: "logout",
        },
        {
          event: "click",
          target: "opener",
          handler: "toggleTooltip",
        },
      ],
    });

    this.onAuthChange = this.onAuthChange.bind(this);
    this.outsideClick = this.outsideClick.bind(this);
  }

  /**
   * @override
   */
  afterComponentRender() {
    this.SSOService.subscribe(this.onAuthChange);
  }

  async onAuthChange({ loggedIn, user }: { loggedIn: boolean; user: UserObject | null }) {
    this.loggedIn = loggedIn;

    toggleClass(this, "fra-state-logged-in", loggedIn);
    toggleClass(this, "fra-state-logged-out", !loggedIn);
    if (loggedIn) {
      await this.I18nService.whenReady;
      if (this.ui.greeting) {
        this.ui.greeting.textContent = replacePlaceHolder(
          this.I18nService.getKey("fraport.travel.components.content.header.login"),
          user!.firstName,
          user!.lastName,
        );
      }
    }
  }

  async logout() {
    await this.SSOService.logOut();
    await this.forceSystemsLogout();
  }

  async forceSystemsLogout() {
    if (this.urls.length === 0) return;

    await Promise.allSettled(this.urls.map(({ url }) => fetch(url, { credentials: "include" })));
    this.redirectToLogin();
  }

  redirectToLogin() {
    // redirect to root login page
    if (this.loginPageUrl) {
      window.location.href = this.loginPageUrl;
    }
  }

  toggleTooltip() {
    // don't open before login state has been determined and its corresponding CTAs shown.
    if (this.loggedIn === undefined) return;
    toggleClass(this.ui.tooltip, HIDDEN);

    if (hasClass(this.ui.tooltip, HIDDEN)) {
      // some buttons in the header are anchor elements with href=# and stopPropagation in event handler!!! so that is why use capture is used
      document.documentElement.removeEventListener("click", this.outsideClick, { capture: true });
    } else {
      document.documentElement.addEventListener("click", this.outsideClick, { capture: true });
    }
  }

  outsideClick(e: UIEvent) {
    const target = e.target as HTMLElement;
    // click was on an <a> or <button> in the tooltip
    const isCTAClick =
      target.closest(".fra-m-user-button__tooltip") &&
      (target.tagName === "A" || target.tagName === "BUTTON" || target.closest("a") || target.closest("button"));

    // ignore clicks which are inside the user-button component on text, etc.
    if (!isCTAClick && target.closest("fra-m-user-button")) return;
    this.toggleTooltip();
  }
}

customElements.define("fra-m-user-button", FRAUserButton);

export default FRAUserButton;
